import React, { Component, lazy } from 'react'
import PropTypes from 'prop-types'
import r2rServicesClient from 'common/utils/r2rServicesClient'

import { omit } from 'common/utils/objectUtils'
import style from './SearchBar.less'
import { PASSENGER_TYPES } from 'tickets/utils/constants.js'
import { contains } from 'common/utils/arrayUtils'
import { FormSuspense } from 'common/components/chips/StyledSuspense'
import BookingsNavBar, { BOOKING_NAV_BAR_STEPS } from
  'common/components/headers/BookingsNavBar'
import {
  Button, IconButton,
  TextSwitcher,
} from 'common/components/inputs'
import R2RFormattedDate from 'common/components/misc/R2RFormattedDate'
import R2RDateTime from 'common/utils/R2RDateTime/R2RDateTime'
import Searchicon from 'styles/icons/search.svg'
import Switch from 'styles/icons/switch-arrows.svg'
import Close from 'styles/icons/close.svg'
import Pencil from 'styles/icons/pencil.svg'
import moment from 'moment'
import classNames from 'classnames'
import Svg from 'tickets/components/common/Svg'
import {
  getStartDatePlaceholderMsg,
  getEndDatePlaceholderMsg,
} from 'common/components/inputs/DateRangePicker/DateRangePickerWrapper.messages'
import {
  ticketSearch,
  travelDatesMsg,
  passengersDiscountsLabelMsg,
  returnTicketMsg,
  passengerMsg,
  getMultiplePassengersMsg,
  oneWayTicketMsg,
  pastErrorMsg,
  noReturnSelectedMsg,
  originMissingMsg,
  destinationMissingMsg,
  agesRequiredMsg,
  updateMsg,
  journeyMsg,
  locationOriginPlaceholderMsg,
  locationDestPlaceholderMsg,
  addReturnTicketMsg,
} from './SearchBar.messages'
import { retry } from 'common/utils/retry'

const DateRangePickerWrapperPromise = import(
  'common/components/inputs/DateRangePicker/DateRangePickerWrapper')
const DateRangePickerWrapper = lazy(() => retry(() => DateRangePickerWrapperPromise))

const SingleDatePickerWrapperPromise = import(
  'common/components/inputs/SingleDatePicker/SingleDatePickerWrapper')
const SingleDatePickerWrapper = lazy(() => retry(() => SingleDatePickerWrapperPromise))

const LocationTextField = lazy(() => retry(() => import('common/components/inputs/LocationTextField')))
const PassengerFilter = lazy(() => retry(() => import('tickets/components/PassengerFilter')))

const DatePicker = (props) => (
  <div className={style.datePicker} data-test="DatePicker">
    <label className={style.formLabel}>
      {travelDatesMsg}
    </label>
    {props.isReturn &&
      <DateRangePickerWrapper
        startDate={props.oDateTime}
        endDate={props.iDateTime}
        onChange={props.onDateRangeChange}
        orientation={props.orientation}
        invalid={(props.validate)}
        locale={props.locale}
        autoFocusEndDate={props.autoFocusEndDate}
        block
        startDatePlaceholderText={getStartDatePlaceholderMsg()}
        endDatePlaceholderText={getEndDatePlaceholderMsg()}
      />
    }
    {!props.isReturn &&
      <div className={style.datePickerWrapper}>
        <SingleDatePickerWrapper
          className={style.singleDatePickerWrapper}
          initialDate={props.oDateTime}
          onChange={props.onDateChange}
          orientation={props.orientation}
          invalid={(props.validate)}
          locale={props.locale}
          block
        />
        <button
          className={style.addReturnTicketButton}
          type="button"
          onClick={props.onAddReturnTicket}
          data-test="addReturnTicketButton"
        >
          {addReturnTicketMsg}
        </button>
      </div>
    }
    {props.validate &&
      <span className={style.formError}>
        {props.validate}
      </span>
    }
  </div>
)

DatePicker.propTypes = {
  isReturn: PropTypes.bool.isRequired,
  oDateTime: PropTypes.object.isRequired,
  iDateTime: PropTypes.object,
  onDateChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onAddReturnTicket: PropTypes.func.isRequired,
  orientation: PropTypes.string.isRequired,
  validate: PropTypes.element,
  locale: PropTypes.string,
  autoFocusEndDate: PropTypes.bool,
}

const Passengers = ({ pax, paxCount, areTravelCards,
  onPassengerOptionsClickDesktop, onPassengerMinus, onPassengerAdd,
  onPassengerUpdate, logEvent, validate, onMobileOpen }) => (
  <div className={style.passengerFilterWrapper}>
    <label className={style.formLabel}>
      {passengersDiscountsLabelMsg}
    </label>
    <PassengerFilter
      onMinus={onPassengerMinus}
      onAdd={onPassengerAdd}
      onMobileOpen={onMobileOpen}
      onPassengerUpdate={onPassengerUpdate}
      pax={pax}
      paxCount={paxCount}
      areTravelCards={areTravelCards}
      onPassengerOptionsClickDesktop={onPassengerOptionsClickDesktop}
      logEvent={logEvent}
      validate={validate}
    />
  </div>
  )

Passengers.propTypes = {
  pax: PropTypes.array.isRequired,
  paxCount: PropTypes.number.isRequired,
  areTravelCards: PropTypes.bool,
  onPassengerOptionsClickDesktop: PropTypes.func,
  onPassengerMinus: PropTypes.func,
  onPassengerAdd: PropTypes.func,
  onPassengerUpdate: PropTypes.func.isRequired,
  validate: PropTypes.string,
  onMobileOpen: PropTypes.func.isRequired,
}

const LocationTextFields = (
  { destination, destinationLng, origin, originLng, onOriginChange,
    onDestinationChange, switchLocations, originInValid, destinationInValid,
    autocompleteEndpoint, languageCode }) =>
  (<div className={style.locationTextFields}>
    <div className={style.locationTextFieldWrapper} data-test="origin">
      <LocationTextField
        text={originLng || origin}
        onChange={onOriginChange}
        className={style.locationTextField}
        invalid={originInValid}
        autocompleteEndpoint={autocompleteEndpoint}
        languageCode={languageCode}
        type="r2r"
        placeholder={locationOriginPlaceholderMsg()}
      />
      <span className={style.switchButton} data-test="switchButton">
        <IconButton
          icon={Switch}
          onClick={switchLocations}
        />
      </span>
    </div>
    <div className={style.locationTextFieldWrapper} data-test="destination">
      <LocationTextField
        text={destinationLng || destination}
        onChange={onDestinationChange}
        className={style.locationTextField}
        invalid={destinationInValid}
        autocompleteEndpoint={autocompleteEndpoint}
        languageCode={languageCode}
        type="r2r"
        placeholder={locationDestPlaceholderMsg()}
      />
    </div>
  </div>
  )

LocationTextFields.propTypes = {
  destination: PropTypes.string,
  destinationLng: PropTypes.string,
  origin: PropTypes.string,
  originLng: PropTypes.string,
  onOriginChange: PropTypes.func,
  onDestinationChange: PropTypes.func,
  switchLocations: PropTypes.func,
  originInValid: PropTypes.element,
  destinationInValid: PropTypes.element,
  autocompleteEndpoint: PropTypes.string,
  languageCode: PropTypes.string,
}

const directionData = [
  {
    label: returnTicketMsg,
    data: 'return',
  },
  {
    label: oneWayTicketMsg,
    data: 'oneway',
  },
]

class SearchBar extends Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    const areRequiredAgesMissing = SearchBar.areRequiredAgesMissing(nextProps.pax)
    if (!areRequiredAgesMissing && prevState.passengersInvalid) {
      return ({ passengersInvalid: null })
    }
    return null
  }

  static areRequiredAgesMissing(passengers) {
    return contains(passengers, p => {
      if (p.type === PASSENGER_TYPES.youth || p.type === PASSENGER_TYPES.senior) {
        return p.age == null
      }
      return false
    })
  }

  constructor(props) {
    super(props)
    const {
      oDateTime, iDateTime,
    } = this.props
    this.mobileFormat = 'ddd D MMM'
    this.orientation = 'horizontal'
    this.state = {
      ...this.props,
      mobileSearch: false,
      oDateTime: (oDateTime) ? moment.utc(oDateTime) : moment.utc(),
      iDateTime: (iDateTime) ? moment.utc(iDateTime) : null,
      autoFocusEndDate: false,
      isUpdateBtnEnabled: false,
    }
  }

  componentDidMount() {
    if (!window.matchMedia('only screen and (min-width: 720px)').matches) {
      this.orientation = 'vertical'
    }
  }

  componentDidUpdate(prevProps) {
    // eslint disabled to ignore having setState in here
    // which is valid according to docs https://reactjs.org/docs/react-component.html#componentdidupdate

    if (prevProps.oDateTime != this.props.oDateTime) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        oDateTime: this.props.oDateTime
          ? moment.utc(this.props.oDateTime)
          : moment.utc(),
      })
    }

    if (prevProps.iDateTime != this.props.iDateTime) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        iDateTime: this.props.iDateTime
          ? moment.utc(this.props.iDateTime)
          : null,
      })
    }

    // if the language has changed, might have new canonicals, long names, or short names
    // so make sure those are reflected in the state
    if (this.hasDestinationInfoUpdated(prevProps, this.props)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        destination: this.props.destination,
        destinationLng: this.props.destinationLng,
        destinationShrt: this.props.destinationShrt,
      })
    }
    if (this.hasOriginInfoUpdated(prevProps, this.props)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        origin: this.props.origin,
        originLng: this.props.originLng,
        originShrt: this.props.originShrt,
      })
    }

    // this code fixes the issue that was happening when you'd do a new search, then press
    // the browser back button, and the inputs would be incorrect
    // known issue: unneeded geocodes will happen because sometimes this will be hit
    // when the language changes
    if (prevProps.destination != this.props.destination) {
      // If the long or short name are still the same, we need to do a geocode)
      if (prevProps.destinationLng == this.props.destinationLng
        || prevProps.destinationShrt == this.props.destinationShrt) {
        const apiOptions = {
          params: {
            key: '86f5qBa1',
            query: this.props.destination,
            languageCode: this.props.languageCode,
          },
          sendAnalyticsIds: false,
        }

        r2rServicesClient.get(this.props.autocompleteEndpoint, apiOptions)
          .then((response) => {
            this.setState({
              destinationLng: response.data.results[0].longName,
              destinationShrt: response.data.results[0].shortName,
            })
          })
      }
    }

    if (prevProps.origin != this.props.origin) {
      if (prevProps.originLng == this.props.originLng
        || prevProps.originShrt == this.props.originShrt) {
        const apiOptions = {
          params: {
            key: '86f5qBa1',
            query: this.props.origin,
            languageCode: this.props.languageCode,
          },
          sendAnalyticsIds: false,
        }

        r2rServicesClient.get(this.props.autocompleteEndpoint, apiOptions)
          .then((response) => {
            this.setState({
              originLng: response.data.results[0].longName,
              originShrt: response.data.results[0].shortName,
            })
          })
      }
    }
  }

  onSetttingsUpdate = (func) => {
    this.setState({
      isUpdateBtnEnabled: true,
    }, () => {
      return func
    })
  }

  onOriginChange = (ob) => {
    this.setState({
      origin: ob.canonicalName,
      originLng: ob.longName,
      originShrt: ob.shortName,
      originKind: ob.kind,
    }, this.checkForPassengerOptions)
    this.props.logEvent('Tickets', 'OriginChanged', ob.canonicalName)
  }

  onDestinationChange = (ob) => {
    this.setState({
      destination: ob.canonicalName,
      destinationLng: ob.longName,
      destinationShrt: ob.shortName,
      destinationKind: ob.kind,
    }, this.checkForPassengerOptions)
    this.props.logEvent('Tickets', 'DestinationChanged', ob.canonicalName)
  }

  onUpdateSearch = () => {
    if (this.isSearchValid()) {
      if (this.state.mobileSearch) {
        this.toggleSearchBar()
      }
      this.setState({
        isUpdateBtnEnabled: false,
      }, () => {
        this.props.updateUrl(this.state, this.props.pax)
      })
    }
  }

  onDateRangeChange = (dateOb) => {
    this.setState({
      oDateTime: dateOb.startDate,
      iDateTime: dateOb.endDate,
      datePickerValidate: null,
    })
    this.props.logEvent('Tickets', 'OnDateRangeChange')
  }

  onDateChange = (dateOb) => {
    this.setState({
      oDateTime: dateOb.date,
      datePickerValidate: null,
    })
    this.props.logEvent('Tickets', 'OnDateSingleChange')
  }

  onAddReturnTicket = () => {
    this.changeIsReturn('return')
    this.setState({ autoFocusEndDate: true })
  }

  getDetailsSt = () => {
    const { iDateTime, oDateTime, isReturn } = this.props

    return (<>
      <R2RFormattedDate
        dateTime={R2RDateTime.fromDate(oDateTime)}
        weekday="short"
        day="numeric"
        month="short"
      />
      {isReturn && ' ⟷ '}
      {isReturn &&
        <R2RFormattedDate
          dateTime={R2RDateTime.fromDate(iDateTime)}
          weekday="short"
          day="numeric"
          month="short"
        />
      }
    </>)
  }

  getPaxSt = () => {
    const { paxCount } = this.props

    return paxCount === 1
      ? passengerMsg
      : getMultiplePassengersMsg(paxCount)
  }

  getMobilePopupStyle = () => (
    (this.state.mobileSearch) ?
      style.searchPopoutOn
      :
      ''
  )

  hasOriginInfoUpdated = (prevProps, currentProps) => {
    const hasCanonicalChanged = prevProps.origin != currentProps.origin
    const hasLongNameChanged = prevProps.originLng != currentProps.originLng
    const hasShortNameChanged = prevProps.originShrt != currentProps.originShrt
    return hasCanonicalChanged || hasLongNameChanged || hasShortNameChanged
  }

  hasDestinationInfoUpdated = (prevProps, currentProps) => {
    const hasCanonicalChanged = prevProps.destination != currentProps.destination
    const hasLongNameChanged = prevProps.destinationLng != currentProps.destinationLng
    const hasShortNameChanged = prevProps.destinationShrt != currentProps.destinationShrt
    return hasCanonicalChanged || hasLongNameChanged || hasShortNameChanged
  }

  isSearchValid = () => {
    let isValid = true
    const { oDateTime, iDateTime, isReturn, destination, origin } = this.state

    if (moment().isAfter(oDateTime, 'day')) {
      isValid = false
      this.setState({
        datePickerValidate: pastErrorMsg,
      })
    }

    if (isReturn && iDateTime === null) {
      isValid = false
      this.setState({
        datePickerValidate: noReturnSelectedMsg,
      })
    }
    if (origin == '') {
      isValid = false
      this.setState({
        originInValid: originMissingMsg,
      })
    }

    if (destination == '') {
      isValid = false
      this.setState({
        destinationInValid: destinationMissingMsg,
      })
    }

    if (SearchBar.areRequiredAgesMissing(this.props.pax)) {
      isValid = false
      this.setState({
        passengersInvalid: agesRequiredMsg,
      })
    }

    if (isValid) {
      this.setState({
        datePickerValidate: null,
        originInValid: null,
        destinationInValid: null,
        passengersInvalid: null,
      })
    }

    return isValid
  }

  checkForPassengerOptions = () => {
    const { origin, destination } = this.state
    if (origin && destination) {
      this.props.onOrigDest(origin, destination)
    }
  }

  switchLocations = () => {
    this.setState({
      destination: this.state.origin,
      destinationLng: this.state.originLng,
      destinationKind: this.state.originKind,
      origin: this.state.destination,
      originLng: this.state.destinationLng,
      originKind: this.state.destinationKind,
    })
    this.props.logEvent('Tickets', 'SwitchLocations')
  }

  changeIsReturn = (direction) => {
    const isReturn = direction === 'return'
    const newState = {
      isReturn,
      datePickerValidate: null,
    }
    if (!isReturn) {
      newState.iDateTime = null
      newState.autoFocusEndDate = false
    }
    this.setState(newState)
    this.props.logEvent('Tickets', 'ChangeIsReturn', (isReturn ? 'return' : 'one-way'))
  }

  handleOverlayClick = () => event => {
    if (event.target === event.currentTarget) {
      if (this.state.mobileSearch) {
        this.toggleSearchBar()
      }
    }
  }

  toggleSearchBar = () => {
    this.setState(prevState => ({
      mobileSearch: !prevState.mobileSearch,
    }))
    this.props.logEvent('Tickets', 'ToggleSearchBar', this.state.mobileSearch ? 'close' : 'open')
  }

  render() {
    const { destination, destinationLng, destinationShrt,
      origin, originLng, originShrt, originInValid, destinationInValid } = this.state
    return (
      <div className={style.main} >
        <BookingsNavBar step={BOOKING_NAV_BAR_STEPS.TICKETS} />
        <div className={style.bottomWrapper}>
          <div
            className={classNames(style.searchPopout, this.getMobilePopupStyle())}
            onClick={this.handleOverlayClick()}
          >
            <div className={style.mobileSearchHeader}>
              <span>{ticketSearch}</span>
              <span className={style.mobileSearchClose} onClick={this.toggleSearchBar}>
                <Svg svg={Close} />
              </span>
            </div>
            <div className={style.innerWrapper} data-test="searchBarIsReturn">
              <TextSwitcher
                data={directionData}
                onChange={(args) => this.onSetttingsUpdate(this.changeIsReturn(args))}
                selectedIndex={this.state.isReturn ? 0 : 1}
              />
            </div>
            <div className={style.innerWrapper}>
              <FormSuspense>
                <LocationTextFields
                  destination={destination}
                  destinationLng={destinationLng}
                  origin={origin}
                  originLng={originLng}
                  onOriginChange={(args) => this.onSetttingsUpdate(this.onOriginChange(args))}
                  onDestinationChange={(args) => this.onSetttingsUpdate(this.onDestinationChange(args))}
                  switchLocations={() => this.onSetttingsUpdate(this.switchLocations())}
                  originInValid={originInValid}
                  destinationInValid={destinationInValid}
                  autocompleteEndpoint={this.props.autocompleteEndpoint}
                  languageCode={this.props.languageCode}
                />
              </FormSuspense>
              <div className={style.datePaxContainer} data-test="searchBarDatePaxUpdate">
                <FormSuspense>
                  <DatePicker
                    oDateTime={this.state.oDateTime}
                    iDateTime={this.state.iDateTime}
                    onDateRangeChange={(args) => this.onSetttingsUpdate(this.onDateRangeChange(args))}
                    onDateChange={(args) => this.onSetttingsUpdate(this.onDateChange(args))}
                    onAddReturnTicket={() => this.onSetttingsUpdate(this.onAddReturnTicket())}
                    orientation={this.orientation}
                    isReturn={this.state.isReturn}
                    validate={this.state.datePickerValidate}
                    locale={this.props.locale}
                    autoFocusEndDate={this.state.autoFocusEndDate}
                  />
                  <Passengers
                    pax={this.props.pax}
                    paxCount={this.props.paxCount}
                    areTravelCards={this.props.areTravelCards}
                    onPassengerOptionsClickDesktop={() => this.onSetttingsUpdate(this.props.onPassengerOptionsClickDesktop())}
                    onPassengerMinus={(args) => this.onSetttingsUpdate(this.props.onPassengerMinus(args))}
                    onPassengerAdd={(args) => this.onSetttingsUpdate(this.props.onPassengerAdd(args))}
                    onPassengerUpdate={(...args) => this.onSetttingsUpdate(this.props.onPassengerUpdate(...args))}
                    validate={this.state.passengersInvalid}
                    logEvent={this.props.logEvent}
                    onMobileOpen={this.onSetttingsUpdate}
                  />
                </FormSuspense>
                <Button
                  display="flex"
                  icon={Searchicon}
                  label={updateMsg}
                  disable={!this.state.isUpdateBtnEnabled}
                  onClick={this.onUpdateSearch}
                />
              </div>
            </div>
          </div>
          {!this.state.mobileSearch &&
            <div className={style.mobileWrapper} onClick={this.toggleSearchBar} >
              <div className={style.mobileDetailBody}>
                <p className={style.mobileLocation}>
                  {journeyMsg((originShrt || origin), (destinationShrt || destination))}
                </p>
                <div className={style.mobileDetails}>
                  <span>{this.getDetailsSt()}</span>
                  <span>{this.getPaxSt()}</span>
                </div>
              </div>
              <span className={style.editButton} data-test="editSearchMobile">
                <Svg svg={Pencil} />
              </span>
            </div>
          }
        </div>
      </div>
    )
  }
}

SearchBar.defaultProps = {
  origin: '',
  destination: '',
}

SearchBar.propTypes = {
  ...omit(Passengers.propTypes, 'onMobileOpen'),
  ...LocationTextField.propTypes,
  oDateTime: PropTypes.string,
  iDateTime: PropTypes.string,
  isReturn: PropTypes.bool,
  origLngName: PropTypes.func,
  destLngName: PropTypes.func,
  updateUrl: PropTypes.func,
  onOrigDest: PropTypes.func,
  onPassengerUpdate: PropTypes.func.isRequired,
  logEvent: PropTypes.func,
  locale: PropTypes.string,
}

export default SearchBar
